import { StrictMode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { isInsideTelegram, signalReady, haptic, bindBackButton, startParam, canFullscreen, isFullscreen, setFullscreen, onFullscreen } from './telegram/call.js'
import { watchTheme } from './theme/theme.js'
import { parseRoute } from './router/deeplink.js'
import { TABS, SCREENS, isVisibleFor, setViewerPerms, type ScreenId, type Tab } from './router/screens.js'
import {
  initialNav, openIn, replaceTop, backIn, pickTab, jumpTo, current, canGoBack, type NavState, type NavResult, type Place,
} from './router/nav.js'
import { fetchSession, type Session } from './api/client.js'
import {
  ScreenSummary, ScreenSku, ScreenReports,
} from './screens/data.js'
import { ScreenAgents, ScreenAgent } from './screens/agents.js'
import { ScreenReport } from './screens/report.js'
import { fetchActionQueue } from './api/sections.js'
import { ScreenAsk, ScreenAskHistory, ScreenAskThread } from './screens/chat.js'
import { ScreenCalc } from './screens/calc.js'
import { ScreenProfit } from './screens/profit.js'
import { ScreenArchive } from './screens/archive.js'
import { ScreenMore, Placeholder, ITEM, GROUPS } from './screens/hub.js'
import { ScreenBlind, ScreenHelp, ScreenPrompts, ScreenPrompt, ScreenOnboarding, ScreenKeyHowto } from './screens/help.js'
import { ScreenAds, ScreenCampaign } from './screens/ads.js'
import { ScreenStock, ScreenPlan } from './screens/stock.js'
import { ScreenFeedback, ScreenFeedbackItem } from './screens/feedback.js'
import { ScreenActions, ScreenAction, ScreenHistory } from './screens/actions.js'
import { ScreenProduct } from './screens/product.js'
import { ScreenMetric, ScreenSearch } from './screens/explore.js'
import { ScreenCosts, ScreenFinanceSettings, ScreenNotify, ScreenTime, ScreenKey, ScreenOps } from './screens/owner.js'
import { ScreenAccess } from './screens/access.js'
import { AnnounceContext, Announcer, Art, HeadContext, RoleContext, useAnnounce, useAnnouncer, type Head } from './screens/common.js'
import { Icon, type IconName } from './components/icons/Icon.js'
import { Mark, Wordmark } from './components/Mark.js'
import artOutside from './assets/illustrations/outside.webp'
import artDenied from './assets/illustrations/denied.webp'
import { useWide } from './wide.js'
import './styles.css'

type Phase =
  | { kind: 'loading' }
  | { kind: 'outside' }                       // S-35
  | { kind: 'denied'; message: string }       // S-36
  | { kind: 'ready'; session: Session }

function useBootstrap(): Phase {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })

  useEffect(() => {
    // [G-A3] Тема красится ВСЕГДА — до и независимо от того, где мы.
    const stop = watchTheme()
    signalReady()

    // [G-A1] Признак — непустой initData, а не наличие объекта Telegram.
    if (!isInsideTelegram()) {
      setPhase({ kind: 'outside' })
      return stop
    }

    void fetchSession().then((r) => {
      if (r.ok) setPhase({ kind: 'ready', session: r.data })
      // 403 — это «доступ ограничен» (S-36), отдельное состояние.
      // 401 — тоже сюда: изнутри Telegram оно означает протухшую строку.
      else setPhase({ kind: 'denied', message: r.error.message })
    })
    return stop
  }, [])

  return phase
}

// ── экраны вне таббара ───────────────────────────────────────────────────────

/** S-00: знак и словесный знак по центру, без «Загрузка…» (04-voice, 3.2). */
function ScreenBoot(): React.JSX.Element {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 8000)
    return () => clearTimeout(t)
  }, [])
  return (
    <div className="app app-bare" data-testid="app" data-screen="S-00">
      <div className="boot" role="status" aria-label="Селлер-Штаб открывается">
        <Mark size={56} />
        <Wordmark />
        {slow && <p className="boot-slow">Дольше обычного. Проверьте интернет или подождите ещё немного.</p>}
      </div>
    </div>
  )
}

/**
 * Имя бота клиент не вписывает: оно приходит из настройки сборки (VITE_BOT_USERNAME).
 * Нет настройки — нет кнопки, остаётся подсказка словами.
 */
const BOT_USERNAME: string | undefined = (import.meta.env['VITE_BOT_USERNAME'] as string | undefined) || undefined

function ScreenOutside(): React.JSX.Element {
  return (
    <div className="app app-bare" data-testid="app" data-screen="S-35">
      <main className="empty">
        <Art src={artOutside} />
        <h1 className="empty-title is-h1">Откройте в Telegram</h1>
        <p className="empty-text">
          Селлер-Штаб показывает данные магазина только внутри Telegram — так он понимает, что это вы.
        </p>
        <p className="empty-text">Откройте чат с ботом и нажмите «Открыть» внизу.</p>
        {BOT_USERNAME !== undefined && (
          <div className="empty-actions">
            <a className="btn" href={`https://t.me/${BOT_USERNAME}`}>Открыть бота</a>
          </div>
        )}
      </main>
    </div>
  )
}

function ScreenDenied({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="app app-bare" data-testid="app" data-screen="S-36">
      <main className="empty">
        <Art src={artDenied} />
        <h1 className="empty-title is-h1">Нет доступа</h1>
        <p className="empty-text" data-testid="denied-message">{message}</p>
        <p className="empty-text">Если вы работаете в магазине, попросите владельца прислать вам приглашение.</p>
      </main>
    </div>
  )
}

// ── навигация ────────────────────────────────────────────────────────────────

type Open = (id: ScreenId, param?: string) => void

const TAB_ICON: Record<Tab, IconName> = {
  T1: 'tab-summary', T2: 'tab-actions', T3: 'tab-products', T4: 'tab-more', T5: 'tab-ask',
}

/** data-testid нажимаемого элемента в фокусе — чтобы вернуться на него «назад». */
function focusedTestId(): string | null {
  const el = document.activeElement
  if (!(el instanceof HTMLElement) || el === document.body) return null
  return el.getAttribute('data-testid')
}

/**
 * Выбор тела экрана. open — вглубь по текущей вкладке; replace — заменить текущий экран
 * (например, «отчёт не найден» → «все отчёты»), не углубляясь.
 */
function ScreenBody(
  { place, role, open, replace }: { place: Place; role: Session['role']; open: Open; replace: Open },
): React.JSX.Element {
  const { spec, param } = place
  switch (spec.id) {
    case 'S-07': return <ScreenSummary onReports={() => open('S-09')} onReport={(id) => open('S-10', id)} onAgents={() => open('S-23')} onHowto={() => open('S-01')} onCalc={(filled) => open(filled ? 'S-44' : 'S-26')}
      onMetric={(code) => open('S-08', code)} />
    case 'S-08': return param === null ? <ScreenMetric code="orders" open={open} /> : <ScreenMetric code={param} open={open} />
    case 'S-11': return <ScreenSku onOpen={(nm) => open('S-12', String(nm))} />
    case 'S-12': return param === null ? <ScreenSku onOpen={(nm) => open('S-12', String(nm))} /> : <ScreenProduct nm={param} open={open} />
    case 'S-38': return <ScreenSearch open={open} />
    case 'S-26': return <ScreenCosts open={open} />
    case 'S-27': return param === null ? <ScreenCosts open={open} /> : <ScreenCalc nm={param} replace={replace} open={open} />
    case 'S-44': return <ScreenProfit period={param} open={open} />
    case 'S-45': return <ScreenArchive />
    case 'S-28': return <ScreenFinanceSettings />
    case 'S-25': return <ScreenNotify open={open} />
    case 'S-41': return <ScreenAsk open={open} />
    case 'S-42': return <ScreenAskHistory open={open} />
    case 'S-43': return param === null ? <ScreenAskHistory open={open} /> : <ScreenAskThread id={param} />
    case 'S-40': return <ScreenTime open={open} />
    case 'S-06': return <ScreenKey />
    case 'S-29': return <ScreenOps />
    case 'S-33': return <ScreenAccess />
    case 'S-01': return <ScreenOnboarding open={open} />
    case 'S-02': case 'S-03': case 'S-04': case 'S-05': return <ScreenKeyHowto open={open} />
    case 'S-20': return <ScreenActions open={open} />
    case 'S-21': return param === null ? <ScreenActions open={open} /> : <ScreenAction id={param} open={open} />
    case 'S-22': return <ScreenHistory open={open} />
    case 'S-09': return <ScreenReports onOpen={(id) => open('S-10', id)} onAgents={() => open('S-23')} />
    case 'S-10': return param === null ? <ScreenReports onOpen={(id) => open('S-10', id)} onAgents={() => open('S-23')} />
      : <ScreenReport id={param} onAll={() => replace('S-09')} onFresh={(id) => replace('S-10', id)} />
    case 'S-37': return <ScreenMore role={role} open={open} />
    case 'S-23': return <ScreenAgents onOpen={(a) => open('S-24', a)} />
    case 'S-24': return param === null ? <ScreenAgents onOpen={(a) => open('S-24', a)} />
      : <ScreenAgent agent={param} onAll={() => replace('S-23')} />
    case 'S-39': return <ScreenBlind open={open} />
    case 'S-32': return <ScreenHelp open={open} />
    case 'S-30': return <ScreenPrompts open={open} />
    case 'S-31': return param === null ? <ScreenPrompts open={open} /> : <ScreenPrompt agent={param} />
    case 'S-13': return <ScreenAds open={open} />
    case 'S-14': return param === null ? <ScreenAds open={open} /> : <ScreenCampaign id={param} onReport={(id) => open('S-10', id)} />
    case 'S-16': return <ScreenStock open={open} />
    case 'S-17': return <ScreenPlan open={open} />
    case 'S-18': return <ScreenFeedback open={open} />
    case 'S-19': return param === null ? <ScreenFeedback open={open} /> : <ScreenFeedbackItem param={param} open={open} />
    default: return <Placeholder spec={spec} onReports={() => open('S-09')} />
  }
}

// ── оболочка ────────────────────────────────────────────────────────────────

/**
 * Таббар. Его настоящую высоту оболочка отдаёт в --wb-tabbar-space: на неё опираются запас
 * под контентом и scroll-padding (фокус не прячется под таббаром, WCAG 2.4.11). При крупном
 * тексте подписи перестают помещаться — таббар становится компактным: видны только иконки,
 * а подписи остаются именами кнопок для диктора (WCAG 1.4.4, ничего не наезжает).
 */
function TabBar({ active, onPick, badge }: { active: Tab; onPick: (t: Tab) => void; badge: Partial<Record<Tab, string>> }): React.JSX.Element {
  const ref = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const nav = ref.current
    if (nav === null) return
    const root = document.documentElement
    let frame = 0
    const fit = (): void => {
      const labels = [...nav.querySelectorAll<HTMLElement>('.tab-label')]
      labels.forEach((l) => l.classList.remove('vh'))
      nav.classList.remove('tabbar-compact')
      const tight = labels.some((l) => l.scrollWidth > l.clientWidth + 1)
      if (tight) { nav.classList.add('tabbar-compact'); labels.forEach((l) => l.classList.add('vh')) }
      root.style.setProperty('--wb-tabbar-space', `${Math.ceil(nav.getBoundingClientRect().height)}px`)
    }
    // В следующем кадре: менять наблюдаемый элемент прямо в колбэке ResizeObserver нельзя (цикл).
    const later = (): void => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit) }
    fit()
    const ro = new ResizeObserver(later)
    ro.observe(nav)
    window.addEventListener('resize', later)
    void document.fonts?.ready.then(later)
    return () => {
      ro.disconnect(); cancelAnimationFrame(frame); window.removeEventListener('resize', later)
      root.style.removeProperty('--wb-tabbar-space')
    }
  }, [])
  return (
    <nav className="tabbar" data-testid="tabbar" aria-label="Разделы" ref={ref}>
      <div className="tabbar-in">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={'tab' + (t.id === active ? ' tab-active' : '')}
            data-testid={'tab-' + t.id}
            aria-current={t.id === active ? 'page' : undefined}
            onClick={() => { haptic('light'); onPick(t.id) }}
          >
            <span className="pill">
              <Icon name={TAB_ICON[t.id]} size={24} />
              {badge[t.id] !== undefined && <span className="tab-badge" aria-hidden="true" data-testid={'badge-' + t.id}>{badge[t.id]}</span>}
            </span>
            <span className="tab-label">{t.title}</span>
            {badge[t.id] !== undefined && <span className="vh">, ждут решения: {badge[t.id]}</span>}
          </button>
        ))}
      </div>
    </nav>
  )
}

/**
 * «Во весь экран» для Telegram Desktop и других клиентов на компьютере (Bot API 8.0): окно мини-аппа там
 * узкое, развёрнутое — включает компьютерную раскладку. Клиент без поддержки ответит fullscreenFailed —
 * кнопка исчезает и подсказывает растянуть окно мышью.
 */
function FullscreenToggle({ variant }: { variant: 'header' | 'side' }): React.JSX.Element | null {
  const [able, setAble] = useState(canFullscreen)
  const [on, setOn] = useState(isFullscreen)
  const announce = useAnnounce()
  useEffect(() => (able ? onFullscreen((e) => {
    if (e === 'failed') { setAble(false); announce({ kind: 'info', text: 'Этот Telegram не разворачивает приложение — растяните окно мышью' }); return }
    setOn(isFullscreen())
  }) : undefined), [able, announce])
  if (!able) return null
  const label = on ? 'Обычное окно' : 'На весь экран'
  return (
    <button type="button" className={variant === 'side' ? 'side-item side-full' : 'hdr-full'} data-testid="fullscreen" onClick={() => setFullscreen(!on)}>
      {label}
    </button>
  )
}

/** Разделы из «Ещё», которые на компьютере видны сразу в боковом меню. */
const SIDE_GROUPS = GROUPS.filter((g) => g.title === 'Отделы штаба' || g.title === 'Настройка')

/**
 * Боковое меню компьютерной раскладки: пять вкладок, как в нижнем меню, и разделы «Ещё» одним нажатием.
 * Запас снизу под таббар здесь не нужен — оболочка обнуляет --wb-tabbar-space.
 */
function SideNav({ nav, role, badge, onPick, onJump, onSearch }: {
  nav: NavState; role: Session['role']; badge: Partial<Record<Tab, string>>
  onPick: (t: Tab) => void; onJump: (id: ScreenId) => void; onSearch: () => void
}): React.JSX.Element {
  useLayoutEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--wb-tabbar-space', '0px')
    return () => { root.style.removeProperty('--wb-tabbar-space') }
  }, [])
  const stack = nav.stacks[nav.active]
  const quick = SIDE_GROUPS.flatMap((g) => g.ids).find((id) => stack.some((p) => p.spec.id === id)) ?? null
  return (
    <nav className="side" data-testid="sidenav" aria-label="Разделы">
      <div className="side-brand"><Mark size={32} /><span>Селлер-Штаб</span></div>
      <button type="button" className="side-item" data-testid="side-search" onClick={onSearch}>
        <Icon name="section-search" size={20} /><span>Поиск</span>
      </button>
      {TABS.map((t) => {
        const on = quick === null && t.id === nav.active
        return (
          <button key={t.id} type="button" className={'side-item' + (on ? ' side-on' : '')} data-testid={'tab-' + t.id}
            aria-current={on ? 'page' : undefined} onClick={() => onPick(t.id)}>
            <Icon name={TAB_ICON[t.id]} size={20} /><span>{t.title}</span>
            {badge[t.id] !== undefined && <span className="side-badge" data-testid={'badge-' + t.id}><span className="vh">ждут решения: </span>{badge[t.id]}</span>}
          </button>
        )
      })}
      {SIDE_GROUPS.map((g) => {
        const ids = g.ids.filter((id) => isVisibleFor(SCREENS[id], role))
        if (ids.length === 0) return null
        return (
          <div key={g.title} className="side-group" role="group" aria-label={g.title}>
            <p className="side-sect wb-overline">{g.title}</p>
            {ids.map((id) => {
              const it = ITEM[id]
              const on = quick === id
              return (
                <button key={id} type="button" className={'side-item' + (on ? ' side-on' : '')} data-testid={'side-' + id}
                  aria-current={on ? 'page' : undefined} onClick={() => onJump(id)}>
                  {it !== undefined && <Icon name={it.icon} size={20} />}<span>{it?.title ?? SCREENS[id].title}</span>
                </button>
              )
            })}
          </div>
        )
      })}
      <div className="side-foot"><FullscreenToggle variant="side" /></div>
    </nav>
  )
}

/** Экраны для чтения — узкой колонкой и на широком окне: длинные строки текста читать тяжело. */
const READ_SCREENS: ReadonlySet<ScreenId> = new Set<ScreenId>(['S-10', 'S-19', 'S-21', 'S-01', 'S-32', 'S-39', 'S-30', 'S-31',
  'S-41', 'S-42', 'S-43', 'S-23', 'S-24', 'S-25', 'S-28', 'S-29', 'S-33', 'S-40', 'S-06', 'S-38'])

function Shell({ session }: { session: Session }): React.JSX.Element {
  const role = session.role
  // Права сотрудника — до первой навигации: по ним прячутся неоткрытые разделы (решение 13.10).
  setViewerPerms(session.perms ?? [])
  const [boot] = useState(() => {
    const route = parseRoute(window.location.href, startParam())
    return initialNav(route.screen, route.id, role)
  })
  const [nav, setNav] = useState<NavState>(boot.state)
  const [head, setHead] = useState<Head>({})
  const { msg, announce } = useAnnouncer()
  const titleRef = useRef<HTMLHeadingElement>(null)
  /** Куда поставить фокус после перехода: заголовок нового экрана или строка, с которой ушли. */
  const focusNext = useRef<{ restore: string | null } | null>(null)

  // Ссылка устарела или раздел только для владельца — говорим об этом, а не открываем молча ([F-7.3.16]).
  useEffect(() => { if (boot.notice !== null) announce({ kind: 'info', text: boot.notice }) }, [boot, announce])

  const go = useCallback((r: NavResult, restore: string | null = null) => {
    if (r.notice !== null) { announce({ kind: 'info', text: r.notice }); return }
    if (r.state === nav) return
    setHead({})
    announce(null)                 // сообщение прошлого экрана к новому не относится
    focusNext.current = { restore }
    setNav(r.state)
    window.scrollTo(0, 0)
  }, [announce, nav])

  const open = useCallback<Open>((id, p) => {
    go(openIn(nav, id, p ?? null, role, focusedTestId()))
  }, [nav, role, go])
  const replace = useCallback<Open>((id, p) => { go(replaceTop(nav, id, p ?? null, role)) }, [nav, role, go])
  const jump = useCallback((id: ScreenId) => { go(jumpTo(nav, id, role)) }, [nav, role, go])
  const wide = useWide()

  const back = useCallback(() => {
    const next = backIn(nav)
    if (next === nav) return
    haptic('light')
    go({ state: next, notice: null }, current(next).from ?? null)
  }, [nav, go])

  const pick = useCallback((t: Tab) => {
    const next = pickTab(nav, t)
    if (next === nav) { window.scrollTo(0, 0); return }
    setHead({})
    announce(null)
    // С вкладки фокус не уводим: таббар не перемонтируется, и человек остаётся там, где был.
    focusNext.current = null
    setNav(next)
    window.scrollTo(0, 0)
  }, [nav, announce])

  // [F-7.3.9], [F-7.3.11] Системная «назад»: видна, когда в стеке вкладки есть куда вернуться.
  const backable = canGoBack(nav)
  useEffect(() => bindBackButton(backable, back), [backable, back])
  // На компьютере «назад» — ещё и Esc. Из поля ввода Esc только снимает фокус: черновик не теряется.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return
      const t = e.target as HTMLElement | null
      if (t !== null && t.matches('input, textarea, select, [contenteditable="true"]')) { t.blur(); return }
      if (backable) back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [backable, back])

  const place = current(nav)

  // Счётчик на вкладке «Действия» — предложения, ждущие решения. Обновляется при каждом переходе:
  // решение на карточке действия должно сразу уменьшить число.
  const [pending, setPending] = useState<number | null>(null)
  useEffect(() => {
    let alive = true
    void fetchActionQueue('pending').then((r) => {
      if (alive && r.ok && typeof r.data.pending_pcs === 'number') setPending(r.data.pending_pcs)
    })
    return () => { alive = false }
  }, [place.spec.id, place.param])
  const badge: Partial<Record<Tab, string>> = pending !== null && pending > 0 ? { T2: pending > 99 ? '99+' : String(pending) } : {}

  /*
   * [WCAG 2.4.3] После перехода фокус — на заголовок нового экрана (диктор объявляет экран, Tab
   * идёт сверху). «Назад» возвращает фокус на строку, с которой ушли; её экран может ещё грузиться —
   * ждём появления до 2 с, потом заголовок. Первая загрузка фокус не трогает.
   */
  useEffect(() => {
    const f = focusNext.current
    if (f === null) return
    // tabindex — только на время фокуса: заголовок не нажимается и в порядке Tab ему не место.
    const toTitle = (): void => {
      focusNext.current = null
      const h = titleRef.current
      if (h === null) return
      h.setAttribute('tabindex', '-1')
      h.addEventListener('blur', () => h.removeAttribute('tabindex'), { once: true })
      h.focus({ preventScroll: true })
    }
    if (f.restore === null) { toTitle(); return }
    const restore = f.restore
    // Строка появляется, когда экран догрузит данные: ловим её вставку в DOM (MutationObserver),
    // а не опрашиваем по таймеру — фокус встаёт сразу, как только строка нарисована.
    const tryFocus = (): boolean => {
      const el = document.querySelector<HTMLElement>(`[data-testid="${CSS.escape(restore)}"]`)
      if (el === null || !el.matches('button, a[href], input, [tabindex]')) return false
      focusNext.current = null; el.focus(); return true
    }
    if (tryFocus()) return
    const mo = new MutationObserver(() => { if (tryFocus()) { mo.disconnect(); clearTimeout(t) } })
    mo.observe(document.body, { childList: true, subtree: true })
    const t = setTimeout(() => { mo.disconnect(); toTitle() }, 2000)
    return () => { mo.disconnect(); clearTimeout(t) }
  }, [nav])

  const spec = place.spec
  const isRoot = !backable
  const title = head.title ?? spec.title

  // [WCAG 2.4.2] У одностраничного приложения заголовок окна меняется вместе с экраном.
  useEffect(() => { document.title = `${title} · Селлер-Штаб` }, [title])

  return (
    <RoleContext.Provider value={role}>
      <HeadContext.Provider value={setHead}>
        <AnnounceContext.Provider value={announce}>
          <div className={'app' + (wide ? ' app-wide' : '')} data-testid="app" data-screen={spec.id} data-depth={nav.stacks[nav.active].length - 1}
            data-layout={READ_SCREENS.has(spec.id) ? 'read' : 'wide'}>
            {/* Своей стрелки «назад» в шапке нет: внутри Telegram «назад» — только системная ([F-7.3.9]). */}
            <header className="hdr">
              <div className="hdr-main">
                {isRoot && !wide && <Mark size={40} title="Селлер-Штаб" />}
                <div className="hdr-text">
                  <h1 className="wb-h1 hdr-title" data-testid="screen-title" ref={titleRef}>{title}</h1>
                  {head.sub != null && head.sub !== '' && <p className="hdr-sub" data-testid="screen-sub">{head.sub}</p>}
                </div>
                <span className="hdr-end">
                {!wide && <FullscreenToggle variant="header" />}
                {!wide && (spec.id === 'S-07' || spec.id === 'S-11') && (
                  <button type="button" className="hdr-act" data-testid="search" aria-label="Поиск" onClick={() => open('S-38')}>
                    <Icon name="section-search" size={24} />
                  </button>
                )}
                <span className="role" data-testid="role">
                  {/* Подпись роли приходит с сервера: «Менеджер» — это сотрудник с правом
                      публиковать ответы, и клиент не должен вычислять это сам. */}
                  <span className="vh">Вы вошли как </span>
                  {(session.role_label ?? (role === 'owner' ? 'Владелец' : 'Сотрудник')).toLowerCase()}
                </span>
                </span>
              </div>
            </header>

            <main key={`${nav.active}:${spec.id}:${place.param ?? ''}`}>
              <ScreenBody place={place} role={role} open={open} replace={replace} />
            </main>

            <Announcer msg={msg} onClose={() => announce(null)} />
            {wide
              ? <SideNav nav={nav} role={role} badge={badge} onPick={pick} onJump={jump} onSearch={() => open('S-38')} />
              : <TabBar active={nav.active} onPick={pick} badge={badge} />}
          </div>
        </AnnounceContext.Provider>
      </HeadContext.Provider>
    </RoleContext.Provider>
  )
}

function App(): React.JSX.Element {
  const phase = useBootstrap()
  switch (phase.kind) {
    case 'loading': return <ScreenBoot />
    case 'outside': return <ScreenOutside />
    case 'denied': return <ScreenDenied message={phase.message} />
    case 'ready': return <Shell session={phase.session} />
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><App /></StrictMode>,
)
